import React from 'react';
import { Tabs, Badge, Button, Progress, Text } from '@mantine/core';
import { ExternalLink, Download } from 'lucide-react';
import { groupByKey } from 'utils/utility';
import { IBookingExternalAPILog } from 'libs/data-access/types';
import { ApiNameAccordion } from './ApiNameAccordion';

interface IProviderTabsProps {
    externalAPILog: { [supplier: string]: IBookingExternalAPILog[] };
    traceId: string;
    isDownloading: boolean;
    isBulkDownloading: boolean;
    downloadProgress: { current: number; total: number } | null;
    onDownload: (mediaId: number, fileName: string) => void;
    onBulkDownload: (providerName: string, logs: IBookingExternalAPILog[]) => void;
}

export const ProviderTabs: React.FC<IProviderTabsProps> = ({
    externalAPILog,
    traceId,
    isDownloading,
    isBulkDownloading,
    downloadProgress,
    onDownload,
    onBulkDownload
}) => {
    return (
        <Tabs
            defaultValue={Object.keys(externalAPILog)[0]}
            color="blue"
            className="w-full"
        >
            <Tabs.List className="mb-4">
                {Object.keys(externalAPILog).map((supplier) => (
                    <Tabs.Tab
                        key={supplier}
                        value={supplier}
                        leftSection={<ExternalLink size={16} />}
                    >
                        <span className="font-medium">{supplier}</span>
                        <Badge
                            variant="light"
                            color="blue"
                            size="sm"
                            className="ml-2"
                        >
                            {externalAPILog[supplier].length}
                        </Badge>
                    </Tabs.Tab>
                ))}
            </Tabs.List>

            {Object.keys(externalAPILog).map((supplier) => {
                const groupedByApiName = groupByKey(externalAPILog[supplier], "apiName");

                return (
                    <Tabs.Panel key={supplier} value={supplier} pt="xs">
                        {/* Download All Button */}
                        <div className="mb-4 flex items-center gap-3">
                            <Button
                                leftSection={<Download size={16} />}
                                variant="filled"
                                color="blue"
                                size="sm"
                                loading={isBulkDownloading}
                                onClick={() => onBulkDownload(supplier, externalAPILog[supplier])}
                            >
                                Download All as ZIP
                            </Button>

                            {isBulkDownloading && downloadProgress && (
                                <div className="flex-1">
                                    <Text size="xs" className="mb-1 text-gray-600">
                                        Downloading {downloadProgress.current} of {downloadProgress.total} files...
                                    </Text>
                                    <Progress
                                        value={(downloadProgress.current / downloadProgress.total) * 100}
                                        size="sm"
                                        color="blue"
                                        animated
                                    />
                                </div>
                            )}
                        </div>

                        <ApiNameAccordion
                            groupedByApiName={groupedByApiName}
                            isDownloading={isDownloading}
                            onDownload={onDownload}
                        />
                    </Tabs.Panel>
                );
            })}
        </Tabs>
    );
};
